import { createStore } from 'vuex';
import { getHttpClient } from '../../config/httpClient';
import toasts from './toasts.js';

// Exported so tests exercise THESE getters rather than a hand-copied
// duplicate. The isAdmin bug (UI-009) lived undetected precisely because
// the store test re-declared its own copy of this logic and then tested
// the copy.
export const getters = {
  isAdmin(state) {
    // Mirrors api/app/Helpers/isAdmin.js: since W0-4 the authoritative
    // signal is users.role === 'admin', and /users/me hides the legacy
    // `admin` boolean entirely, so checking only the boolean meant NO
    // session ever counted as admin client-side (UI-009). The boolean
    // remains as a compatibility fallback until the column is dropped.
    if (!state.user) return false;
    if (state.user.role === 'admin') return true;
    const admin = state.user.admin;
    return admin === 1 || admin === '1' || admin === true;
  },
  // Spec 13: confirming a driver's proof of delivery and sending a document
  // for signature are dispatcher work, not administrator work, and the API
  // gates both with RequireRole(['admin', 'dispatcher', ...]). isAdmin is too
  // narrow for those surfaces and role checks scattered through components
  // would drift, so the one reading of "may act as a dispatcher" lives here.
  canDispatch(state, storeGetters) {
    if (storeGetters.isAdmin) return true;
    return !!(state.user && state.user.role === 'dispatcher');
  },
  // Mark, 2026-10-29 (spec 53): the books are the accountant's job, so an
  // accountant connects them and maps their items as an admin does
  // (UserAccessService `accounting.connect`), and sends a document for
  // signature as a dispatcher does (the documents routes already admit
  // every office role).
  canManageBooks(state, storeGetters) {
    if (storeGetters.isAdmin) return true;
    return !!(state.user && state.user.role === 'accountant');
  },
  canSendDocuments(state, storeGetters) {
    if (storeGetters.canDispatch) return true;
    return !!(state.user && state.user.role === 'accountant');
  },
  user(state) {
    return state.user;
  },
  // spec 12 Part 4: main.js swallows a failed fetchOrganization with
  // `.catch(() => {})`, so state.organization === null is a real, reachable
  // state (not just the pre-fetch instant), and every getter below tolerates it.
  billingStatus(state) {
    return (state.organization && state.organization.billing_status) || null;
  },
  // The public demo (spec 25 section 3.6): GET /organization carries a `demo`
  // object there and nowhere else, naming what the demo API refuses. A key it
  // does not mention is allowed, so every real tenant answers true to both.
  isDemo(state) {
    return Boolean(state.organization && state.organization.demo);
  },
  // Spec 53 4.1: a practice fleet is a demo lease that carries a training
  // enrollment (`demo.training`). The object, or null on every other lease
  // and every real tenant.
  trainingLease(state) {
    const demo = state.organization && state.organization.demo;
    return (demo && demo.training) || null;
  },
  // Spec 53 3.3: GET /organization's `training` object for the session user,
  // {modules, required, gated, cleared_at, outstanding}, or null before it
  // lands (and on a failed fetch, which main.js swallows).
  training(state) {
    return (state.organization && state.organization.training) || null;
  },
  // Whether the session user is gated: required training outstanding, so the
  // account stays shut and the app shows the training gate, never the shell.
  // The organization's answer when it has arrived; until then the /users/me
  // row, which carries the same two columns the server decides it from
  // (Training/required.js isGated). main.js mounts before the organization
  // lands, and the shell must not mount for a gated user even for that
  // instant, because everything in it fires requests that answer 403.
  trainingGated(state) {
    const training = state.organization && state.organization.training;
    if (training && typeof training.gated === 'boolean') return training.gated;
    const user = state.user;
    return Boolean(user && user.training_required === true && !user.training_cleared_at);
  },
  // Training is offered to this (ungated) user: the header's Training
  // button. On by the org's `training.modules` setting; never on the
  // anonymous demo, whose organization carries no training object.
  trainingOffered(state, storeGetters) {
    const training = storeGetters.training;
    return Boolean(training && training.modules && !storeGetters.trainingGated);
  },
  demoAllows(state) {
    return capability => {
      const demo = state.organization && state.organization.demo;
      return !(demo && demo[capability] === false);
    };
  },
  // Mark, 2026-10-11: Send invoice only when the invoicing accounting
  // provider sends its own invoice email (QuickBooks today). GET /organization
  // decides it from the adapter's capability, the same one the send route
  // refuses on; Ladeway never emails an invoice itself. No organization (a
  // failed boot fetch) means no provider is known, so nothing is offered.
  canSendInvoice(state) {
    const invoicing = state.organization && state.organization.invoicing;
    return Boolean(invoicing && invoicing.can_send);
  },
  // The server computes this tier ('none' | 'admin' | 'all'); the client must
  // not derive it from pilot_ends_at or any other date arithmetic.
  billingBanner(state) {
    return (state.organization && state.organization.billing_banner) || 'none';
  },
  showBillingBanner(state, getters) {
    return (
      getters.billingBanner === 'all' || (getters.billingBanner === 'admin' && getters.isAdmin)
    );
  },
  billingBannerText(state, getters) {
    if (!getters.showBillingBanner) return '';
    const reason = state.organization && state.organization.billing_banner_reason;
    if (reason === 'closed') {
      return 'This Ladeway account is closed. An administrator can still download its data from the Subscription page.';
    }
    if (reason === 'no_payment_method') {
      return 'Your Ladeway pilot has ended. Add a payment method to continue; access comes back as soon as you do.';
    }
    if (reason === 'pilot_ending') {
      const ends = state.organization.pilot_ends_at
        ? new Date(state.organization.pilot_ends_at).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
          })
        : null;
      return `Your free pilot ends${ends ? ` ${ends}` : ' soon'}. Add a payment method so nothing is interrupted.`;
    }
    if (getters.billingStatus === 'suspended') {
      return 'Your Ladeway subscription is suspended. Operations are paused until it is paid.';
    }
    let text = 'Your Ladeway subscription payment is overdue.';
    if (getters.billingBanner === 'all') {
      text += ' Access will be suspended if it is not paid.';
    }
    return text;
  },
};

const store = createStore({
  // The toast queue is a namespaced module rather than more root state: it is
  // the notification owner from refresh_plan.md 3.1.7, it has its own timers,
  // and nothing else in the app should be able to reach into its array. The
  // shell mounts one LwToastRegion that reads it (app.vue).
  modules: { toasts },

  state: {
    user: null,
    organization: null,
    customers: [],
    drivers: [],
    trucks: [],
    locations: [],
  },

  mutations: {
    setUser(state, user) {
      state.user = user;
    },
    setOrganization(state, organization) {
      state.organization = organization;
    },
    setCustomers(state, customers) {
      state.customers = customers;
    },
    setDrivers(state, drivers) {
      state.drivers = drivers;
    },
    setTrucks(state, trucks) {
      state.trucks = trucks;
    },
    setLocations(state, locations) {
      state.locations = locations;
    },
  },

  actions: {
    // W0-4: the operating company's identity (name, legal name for printed
    // documents). Carrier branding is data, not design.
    async fetchOrganization({ commit }) {
      try {
        const res = await getHttpClient().get('/organization');
        commit('setOrganization', res.data);
        return res.data;
      } catch (err) {
        console.error('Failed to fetch organization:', err.message);
        throw err;
      }
    },
    async fetchCustomers({ commit }) {
      try {
        const res = await getHttpClient().get('/customers');
        commit('setCustomers', res.data);
        return res.data;
      } catch (err) {
        console.error('Failed to fetch customers:', err.message);
        throw err;
      }
    },
    async fetchDrivers({ commit }) {
      try {
        const res = await getHttpClient().get('/drivers');
        commit('setDrivers', res.data);
        return res.data;
      } catch (err) {
        console.error('Failed to fetch drivers:', err.message);
        throw err;
      }
    },
    async fetchTrucks({ commit }, { filter } = {}) {
      try {
        let url = '/trucks';
        if (filter === 'active') url = '/trucks/active';
        else if (filter === 'inactive') url = '/trucks/inactive';
        const res = await getHttpClient().get(url);
        commit('setTrucks', res.data);
        return res.data;
      } catch (err) {
        console.error('Failed to fetch trucks:', err.message);
        throw err;
      }
    },
    async fetchLocations({ commit }) {
      try {
        const res = await getHttpClient().get('/locations');
        commit('setLocations', res.data);
        return res.data;
      } catch (err) {
        console.error('Failed to fetch locations:', err.message);
        throw err;
      }
    },
  },

  getters,
});

export default store;
